import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

const UpdateEmail: React.FC = () => {
  const { user, signOut, updateCustomEmail } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/', { state: { from: { pathname: '/dashboard' } } });
    } catch (err) {
      setError('Failed to sign out. Please try again.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newEmail || !password) {
      return setError('Please fill in all fields');
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      return setError('Please enter a valid email address');
    }

    if (newEmail === user?.email) {
      return setError('New email must be different from current email');
    }

    try {
      setError('');
      setSuccess('');
      setLoading(true);

      // First, reauthenticate the user
      if (!user) throw new Error('No user signed in');
      const credential = EmailAuthProvider.credential(
        user.email!,
        password
      );
      await reauthenticateWithCredential(user, credential);

      // Then send verification email
      await updateCustomEmail(newEmail);
      setVerificationSent(true);
      setSuccess(
        'A verification email has been sent to your new email address. After clicking the verification link, please sign out and sign back in with your new email address to complete the process.'
      );
      setNewEmail('');
      setPassword('');
    } catch (err) {
      console.error('Failed to update email:', err);
      if (err instanceof Error && 'code' in err) {
        if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
          setError('Invalid password. Please try again.');
        } else if (err.code === 'auth/requires-recent-login') {
          setError(
            'This operation requires recent authentication. Please log out and log back in before retrying.'
          );
        } else if (err.code === 'auth/email-already-in-use') {
          setError('This email address is already in use by another account. Please use a different email address.');
        } else {
          setError('Failed to update email. Please try again.');
        }
      } else {
        setError('Failed to update email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const containerStyle = {
    maxWidth: '400px',
    margin: '40px auto',
    padding: theme.spacing.LARGE,
    backgroundColor: theme.colors.SURFACE,
    borderRadius: '8px',
    border: `1px solid ${theme.colors.BORDER}`,
  };

  const inputStyle = {
    width: '100%',
    padding: theme.spacing.SMALL,
    marginBottom: theme.spacing.MEDIUM,
    border: `1px solid ${theme.colors.INPUT_BORDER}`,
    borderRadius: '4px',
    fontSize: theme.fonts.MEDIUM,
    backgroundColor: theme.colors.INPUT_BACKGROUND,
    color: theme.colors.INPUT_TEXT,
  };

  const buttonStyle = {
    width: '100%',
    padding: theme.spacing.MEDIUM,
    backgroundColor: theme.colors.BUTTON_PRIMARY_BG,
    color: theme.colors.BUTTON_PRIMARY_TEXT,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    backgroundColor: theme.colors.SECONDARY,
    marginTop: theme.spacing.SMALL,
  };

  const signOutButtonStyle = {
    ...buttonStyle,
    backgroundColor: theme.colors.PRIMARY,
    marginTop: theme.spacing.MEDIUM,
  };

  return (
    <div style={containerStyle}>
      <style>
        {`
          input::placeholder {
            color: rgba(0, 0, 0, 0.6) !important;
            opacity: 1;
          }
        `}
      </style>
      <h2
        style={{
          color: theme.colors.TEXT,
          marginBottom: theme.spacing.LARGE,
          textAlign: 'center',
        }}
      >
        Update Email
      </h2>

      {error && (
        <div
          style={{
            backgroundColor: theme.colors.ERROR,
            color: theme.colors.WHITE,
            padding: theme.spacing.MEDIUM,
            borderRadius: '4px',
            marginBottom: theme.spacing.MEDIUM,
            textAlign: 'center',
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            backgroundColor: theme.colors.BACKGROUND,
            color: theme.colors.TEXT,
            padding: theme.spacing.MEDIUM,
            borderRadius: '4px',
            marginBottom: theme.spacing.MEDIUM,
            textAlign: 'center',
            border: `1px solid ${theme.colors.SUCCESS}`,
            fontSize: '0.9rem',
          }}
        >
          {success}
        </div>
      )}

      {!verificationSent ? (
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: theme.spacing.MEDIUM }}>
            <label
              style={{
                display: 'block',
                marginBottom: theme.spacing.SMALL,
                color: theme.colors.TEXT_SECONDARY,
              }}
            >
              Current Email
            </label>
            <div
              style={{
                padding: theme.spacing.SMALL,
                backgroundColor: theme.colors.BACKGROUND,
                border: `1px solid ${theme.colors.BORDER}`,
                borderRadius: '4px',
                color: theme.colors.TEXT,
              }}
            >
              {user?.email}
            </div>
          </div>
          <input
            type="email"
            placeholder="New Email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            style={inputStyle}
            required
          />
          <input
            type="password"
            placeholder="Current Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
            required
          />
          <div
            style={{
              backgroundColor: theme.colors.BACKGROUND,
              padding: theme.spacing.MEDIUM,
              borderRadius: '4px',
              marginBottom: theme.spacing.MEDIUM,
              fontSize: '0.9rem',
              color: theme.colors.TEXT_SECONDARY,
              border: `1px solid ${theme.colors.BORDER}`,
            }}
          >
            Changing your email will require clicking a verification link in your new email address.
          </div>
          <button type="submit" disabled={loading} style={buttonStyle}>
            {loading ? 'Sending Verification...' : 'Update Email'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/profile')}
            style={cancelButtonStyle}
          >
            Cancel
          </button>
        </form>
      ) : (
        <button onClick={handleSignOut} style={signOutButtonStyle}>
          Sign Out and Login with New Email
        </button>
      )}
    </div>
  );
};

export default UpdateEmail;
